import React, { useState } from "react";
import {
    Box, Divider, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader,
    DrawerOverlay, Stack, Text, useColorModeValue,
} from "@chakra-ui/react";
import { getSocialLinks } from "./shared/certificationUtils";
import DrawerDetailHeader from "./shared/DrawerDetailHeader";
import DocumentsGrid from "./shared/DocumentsGrid";
import IdentityCompare from "./shared/IdentityCompare";
import StripeTransferPanel from "./shared/StripeTransferPanel";
import DrawerCollapsibles from "./shared/DrawerCollapsibles";
import CertificationActionPanel from "./shared/CertificationActionPanel";
import ImageViewerDialog from "./shared/ImageViewerDialog";
import { MissingFieldsBox } from "./shared/DetailHeaderParts";

/**
 * Documents section: a title followed by the thumbnail grid. Always visible as
 * it is the primary evidence the admin reviews.
 * @param {Object} props
 * @param {Object} props.certification - Certification data
 * @param {Function} props.onImageClick - Handler to open a document full-screen
 * @returns {React.ReactElement}
 */
function DocumentsSection({ certification, onImageClick }) {
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Box>
            <Text fontSize="sm" fontWeight="700" color={titleColor} mb="10px">Documents</Text>
            <DocumentsGrid documents={certification.documents}
                documentUrls={certification.document_urls} onImageClick={onImageClick} />
        </Box>
    );
}

/**
 * Full body of the detail drawer, ordered by review priority: Stripe issues,
 * prior missing-fields context, documents, declared identity, then collapsed
 * secondary sections, then the decision panel.
 * @param {Object} props
 * @param {Object} props.certification - Certification data
 * @param {Function} props.onImageClick - Document click handler
 * @param {Function} props.onUpdated - Update callback after a decision
 * @param {Function} props.onClose - Drawer close handler
 * @returns {React.ReactElement}
 */
function DrawerBodyContent({ certification, onImageClick, onUpdated, onClose }) {
    return (
        <Stack spacing="16px">
            <StripeTransferPanel certification={certification} onCertRefreshed={onUpdated} />
            <MissingFieldsBox certification={certification} />
            <DocumentsSection certification={certification} onImageClick={onImageClick} />
            <IdentityCompare certification={certification} />
            <DrawerCollapsibles certification={certification} socialLinks={getSocialLinks(certification)} />
            <Divider />
            <CertificationActionPanel key={certification.id} certification={certification}
                onUpdated={onUpdated} onClose={onClose} />
        </Stack>
    );
}

/**
 * Right-side drawer showing the full detail of a certification with progressive
 * disclosure and the approve / request-correction decision panel.
 * @param {Object} props
 * @param {Object|null} props.certification - Selected certification (null = closed)
 * @param {Function} props.onClose - Callback to close the drawer
 * @param {Function} props.onUpdated - Callback receiving an updated cert payload
 * @returns {React.ReactElement}
 */
function CertificationDrawer({ certification, onClose, onUpdated }) {
    const [imageOpen, setImageOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const isOpen = Boolean(certification?.id);
    const onImageClick = (url) => { setSelectedImage(url); setImageOpen(true); };
    return (
        <>
            <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader>{isOpen && <DrawerDetailHeader certification={certification} />}</DrawerHeader>
                    <DrawerBody>
                        {isOpen && (
                            <DrawerBodyContent certification={certification} onImageClick={onImageClick}
                                onUpdated={onUpdated} onClose={onClose} />
                        )}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
            <ImageViewerDialog open={imageOpen} onClose={() => setImageOpen(false)} imageUrl={selectedImage} />
        </>
    );
}

export default CertificationDrawer;
